import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Button } from '@/shared/ui/buttons/Button';
import { Input } from '@/shared/ui/inputs/Input';
import { Select } from '@/shared/ui/selects/Select';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { useCategories } from '@/entities/categories';
import {
  apiParcelsProductsPost,
  apiParcelsProductsPut,
} from '@/entities/parcels-products';
import { categoryLabel } from '@/features/parcel-products/modules/lib/categoryLabel';
import {
  emptyProductValues,
  productSchema,
  type ProductFormValues,
} from '@/features/parcel-products/modules/types';
import type { ApiCategoriesGetItem } from '@/shared/types/api/categories';
import type {
  ApiParcelsProductsGetItem,
  ApiParcelsProductsPostBody,
} from '@/shared/types/api/parcels-products';

type Props = {
  open: boolean;
  parcelId: string;
  editing: ApiParcelsProductsGetItem | null;
  onClose: () => void;
};

const toBody = (v: ProductFormValues): ApiParcelsProductsPostBody => ({
  category_id: v.category_id,
  subcategory_id: v.subcategory_id,
  full_name: v.full_name,
  quantity: v.quantity,
  price_per_item: v.price_per_item,
});

export const FeaturesParcelProductsFormDialog = ({ open, parcelId, editing, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const categoriesQuery = useCategories();
  const categories: ApiCategoriesGetItem[] = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data],
  );
  const lang = i18n.resolvedLanguage ?? i18n.language;

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: emptyProductValues,
    mode: 'onSubmit',
  });
  const { control, register, reset, setValue, formState } = form;
  const errors = formState.errors;
  const translateError = (msg?: string) => (msg ? t(msg) : undefined);

  useEffect(() => {
    if (!open) return;
    reset(
      editing
        ? {
            category_id: editing.category_id,
            subcategory_id: editing.subcategory_id,
            full_name: editing.full_name,
            quantity: editing.quantity,
            price_per_item: editing.price_per_item,
          }
        : emptyProductValues,
    );
  }, [open, editing, reset]);

  const selectedCategoryId = useWatch({ control, name: 'category_id' });
  const subcategories = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId)?.subcategories ?? [],
    [categories, selectedCategoryId],
  );

  const categoryOptions = useMemo(
    () => [
      { value: '', label: t('parcels.products.form.categoryPlaceholder') },
      ...categories.map((c) => ({ value: c.id, label: categoryLabel(c, lang) })),
    ],
    [categories, lang, t],
  );
  const subcategoryOptions = useMemo(
    () => [
      { value: '', label: t('parcels.products.form.subcategoryPlaceholder') },
      ...subcategories.map((s) => ({ value: s.id, label: categoryLabel(s, lang) })),
    ],
    [subcategories, lang, t],
  );

  const save = useMutate({
    api: (body: ApiParcelsProductsPostBody) =>
      editing
        ? apiParcelsProductsPut(parcelId, editing.id, body)
        : apiParcelsProductsPost(parcelId, body),
    invalidateKeys: [queryKeys.parcels.all, queryKeys.parcels.products(parcelId), queryKeys.pinfl.all],
    onSuccess: () => {
      toast.success(editing ? t('parcels.products.toast.updated') : t('parcels.products.toast.added'));
      onClose();
    },
    onError: (_e, message) => toast.error(message),
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!save.isPending) onClose();
      }}
      title={editing ? t('parcels.products.form.editTitle') : t('parcels.products.form.addTitle')}
      size="lg"
      closeOnBackdrop={!save.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {t('parcels.products.form.cancel')}
          </Button>
          <Button
            loading={save.isPending}
            onClick={() => void form.handleSubmit((v) => save.mutate(toBody(v)))()}
          >
            {t('parcels.products.form.save')}
          </Button>
        </>
      }
    >
      {categoriesQuery.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={form.handleSubmit((v) => save.mutate(toBody(v)))}
        >
          <Controller
            control={control}
            name="category_id"
            render={({ field }) => (
              <Select
                label={t('parcels.products.form.category')}
                required
                options={categoryOptions}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  setValue('subcategory_id', '');
                }}
                error={translateError(errors.category_id?.message)}
              />
            )}
          />
          <Controller
            control={control}
            name="subcategory_id"
            render={({ field }) => (
              <Select
                label={t('parcels.products.form.subcategory')}
                required
                options={subcategoryOptions}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                disabled={!selectedCategoryId}
                error={translateError(errors.subcategory_id?.message)}
              />
            )}
          />
          <Input
            label={t('parcels.products.form.fullName')}
            required
            error={translateError(errors.full_name?.message)}
            {...register('full_name')}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label={t('parcels.products.form.quantity')}
              required
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              error={translateError(errors.quantity?.message)}
              {...register('quantity')}
            />
            <Input
              label={t('parcels.products.form.pricePerItem')}
              required
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              error={translateError(errors.price_per_item?.message)}
              {...register('price_per_item')}
            />
          </div>
        </form>
      )}
    </Dialog>
  );
};
